import type { ExplorerFileRecordCopyPayload } from "./payloads";

export interface ValidatedMove {
  recordId: string;
  sourcePath: string;
  destinationPath: string;
  destinationParentPath: string;
  isFolder: boolean;
  sourceName: string;
}

export type MoveRejection =
  | { kind: "self_folder"; recordId: string; sourcePath: string }
  | { kind: "wrong_scope"; recordId: string };

export interface ValidatedMoveBatch {
  moves: ValidatedMove[];
  rejections: MoveRejection[];
  /** True when at least one payload was a same-parent no-op (silent skip). */
  noopCount: number;
}

/**
 * Convert a raw payload batch into validated workspace move intents.
 *
 * Allows cross-workspace moves (D-XWS): workspace-class payloads relocate
 * to the destination workspace rather than being rejected as wrong scope.
 *
 * - Filters out drops onto the record's existing parent in the same workspace.
 * - Rejects folder moves into the folder itself or any descendant.
 * - Skips non-workspace payloads; the classifier routes those to copy.
 */
export function buildWorkspaceMoves(
  payloads: ExplorerFileRecordCopyPayload[],
  destination: { workspaceId: string; parentPath: string },
): ValidatedMoveBatch {
  const moves: ValidatedMove[] = [];
  const rejections: MoveRejection[] = [];
  let noopCount = 0;

  for (const p of payloads) {
    if (p.sourceClass !== "workspace" || !p.sourceWorkspaceId) {
      rejections.push({ kind: "wrong_scope", recordId: p.recordId });
      continue;
    }

    if (
      p.isFolder &&
      p.sourceWorkspaceId === destination.workspaceId &&
      destination.parentPath.startsWith(p.sourcePath)
    ) {
      rejections.push({
        kind: "self_folder",
        recordId: p.recordId,
        sourcePath: p.sourcePath,
      });
      continue;
    }

    const sourceName = leafName(p.sourcePath);
    const destinationPath = buildDestinationPath(
      destination.parentPath,
      sourceName,
      p.isFolder,
    );

    if (
      destinationPath === p.sourcePath &&
      p.sourceWorkspaceId === destination.workspaceId
    ) {
      noopCount += 1;
      continue;
    }

    moves.push({
      recordId: p.recordId,
      sourcePath: p.sourcePath,
      destinationPath,
      destinationParentPath: destination.parentPath,
      isFolder: p.isFolder,
      sourceName,
    });
  }

  return { moves, rejections, noopCount };
}

/** @deprecated Use `buildWorkspaceMoves` — cross-workspace moves are unified. */
export const buildRailWorkspaceMoves = buildWorkspaceMoves;

/**
 * Construct the full destination path for a record being moved into
 * `parentPath`. Mirrors the backend's folder-marker convention:
 * folders carry a trailing `/`, files do not.
 */
function buildDestinationPath(
  parentPath: string,
  sourceName: string,
  isFolder: boolean,
): string {
  if (parentPath) {
    return `${parentPath}${isFolder ? `${sourceName}/` : sourceName}`;
  }
  return isFolder ? `${sourceName}/` : sourceName;
}

export function leafName(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments[segments.length - 1] || path;
}

/** Split rail file payloads into workspace moves vs cross-class copies. */
export function partitionRailFilePayloads(
  payloads: ExplorerFileRecordCopyPayload[],
): {
  workspace: ExplorerFileRecordCopyPayload[];
  crossClassCopy: ExplorerFileRecordCopyPayload[];
} {
  const workspace: ExplorerFileRecordCopyPayload[] = [];
  const crossClassCopy: ExplorerFileRecordCopyPayload[] = [];
  for (const p of payloads) {
    if (p.sourceClass === "workspace") {
      workspace.push(p);
    } else if (p.sourceClass === "canvas" || p.sourceClass === "flow-session") {
      crossClassCopy.push(p);
    }
  }
  return { workspace, crossClassCopy };
}
